import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  REFERENCE_DESCRIPTION_MAX,
  REFERENCE_NOTE_MAX,
  REFERENCE_TITLE_MAX,
  REFERENCE_URL_MAX,
  TAB_SLUGS,
  type CreateReferenceInput,
  type ReferenceCategory,
  type ReferenceLibraryMeta,
  type ReferenceSharedMetadata,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { SideDialog } from '../team/SideDialog.js';
import { ADD_MODE_LABELS, type AddMode } from './EmptyLibrary.js';
import { CategorySelect, GroupChooser, LinksEditor, TagEditor, type EditableLink } from './Pickers.js';
import { acceptAttribute, formatBytes, looksLikeUrl, uploadLimitText } from './format.js';
import { errorText, newRequestKey, useDebounced, useInvalidateReferences } from './hooks.js';
import type { BulkUpload, UploadItem } from './useBulkUpload.js';

/** A pasted "vimeo.com/123" is sent as a URL the server can fetch. */
export function withScheme(url: string): string {
  const trimmed = url.trim();
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * THE ADD DRAWER — the one place a reference is described and saved (design §6),
 * opened by the header's Add reference, the quick-add bar and the empty
 * library's tiles. Link | Upload | Note, no wizard; the category, groups, tags
 * and links underneath are kept when switching between them and, for an upload,
 * applied to every file.
 *
 * Every create carries a `clientRequestId`. A save retried with the same fields
 * sends the same key, so a lost answer cannot make the reference twice; a save
 * with different fields mints a new one, because the server refuses a changed
 * payload under an old key.
 */
export function AddReferenceDrawer({
  projectId,
  initialMode,
  initialUrl,
  initialGroupIds,
  meta,
  bulk,
  onCreated,
  onClose,
}: {
  projectId: string;
  initialMode: AddMode;
  initialUrl?: string;
  initialGroupIds: readonly string[];
  meta: ReferenceLibraryMeta | undefined;
  bulk: BulkUpload;
  onCreated: (referenceId: string) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<AddMode>(initialMode);
  const [category, setCategory] = useState<ReferenceCategory | null>(null);
  const [groupIds, setGroupIds] = useState<string[]>([...initialGroupIds]);
  const [tags, setTags] = useState<string[]>([]);
  const [links, setLinks] = useState<EditableLink[]>([]);
  const groupsLabel = useId();
  const sharedTitle = useId();

  const shared: ReferenceSharedMetadata = {
    category,
    groupIds,
    tags,
    links: links.map((l) => ({ targetType: l.targetType, targetId: l.targetId })),
  };

  const sharedFields: ReactNode =
    mode === 'UPLOAD' && meta?.upload.allowed === false ? null : (
      <section className="ref-shared" aria-labelledby={sharedTitle} data-testid="ref-add-shared">
        <h3 className="ref-shared-title" id={sharedTitle}>
          {mode === 'UPLOAD' ? 'Applied to every file' : 'Organise it'}
        </h3>
        <div className="ref-field">
          <label className="flbl" htmlFor="ref-add-category">
            Category
          </label>
          <CategorySelect id="ref-add-category" value={category} onChange={setCategory} testId="ref-add-category" />
        </div>
        <div className="ref-field">
          <p className="flbl" id={groupsLabel}>
            Groups
          </p>
          <GroupChooser
            groups={meta?.groups ?? []}
            value={groupIds}
            onChange={setGroupIds}
            testId="ref-add-groups"
            labelId={groupsLabel}
          />
        </div>
        <div className="ref-field">
          <p className="flbl">Tags</p>
          <TagEditor tags={tags} suggestions={(meta?.tags ?? []).map((t) => t.label)} onChange={setTags} testId="ref-add-tags" />
        </div>
        <div className="ref-field">
          <p className="flbl">Linked to</p>
          <LinksEditor
            projectId={projectId}
            links={links}
            editable
            onAdd={(option) => {
              setLinks((current) =>
                current.some((l) => l.targetType === option.targetType && l.targetId === option.targetId)
                  ? current
                  : [...current, { key: `${option.targetType}:${option.targetId}`, ...option }],
              );
            }}
            onRemove={(link) => {
              setLinks((current) => current.filter((l) => l.key !== link.key));
            }}
            testId="ref-add-links"
          />
        </div>
      </section>
    );

  return (
    <SideDialog title="Add reference" testId="ref-add-drawer" size="lg" onClose={onClose}>
      <div className="ref-modes" role="group" aria-label="What to add">
        {(['LINK', 'UPLOAD', 'NOTE'] as const).map((m) => (
          <button
            key={m}
            type="button"
            className="ref-mode"
            aria-pressed={mode === m}
            data-testid={`ref-add-mode-${m}`}
            onClick={() => {
              setMode(m);
            }}
          >
            <Icon name={m === 'LINK' ? 'link' : m === 'UPLOAD' ? 'upload' : 'note'} size={15} />
            {m === 'UPLOAD' ? 'Upload' : ADD_MODE_LABELS[m]}
          </button>
        ))}
      </div>

      {mode === 'LINK' ? (
        <LinkForm
          projectId={projectId}
          initialUrl={initialUrl ?? ''}
          shared={shared}
          sharedFields={sharedFields}
          onCreated={onCreated}
          onClose={onClose}
        />
      ) : mode === 'NOTE' ? (
        <NoteForm projectId={projectId} shared={shared} sharedFields={sharedFields} onCreated={onCreated} onClose={onClose} />
      ) : (
        <UploadForm projectId={projectId} meta={meta} bulk={bulk} shared={shared} sharedFields={sharedFields} onClose={onClose} />
      )}

    </SideDialog>
  );
}

/** One key per distinct payload: the same fields retried reuse it, changed fields get a new one. */
function useRequestKey() {
  const key = useRef(newRequestKey());
  const sent = useRef<string | null>(null);
  return (payload: unknown): string => {
    const shape = JSON.stringify(payload);
    if (sent.current !== null && sent.current !== shape) key.current = newRequestKey();
    sent.current = shape;
    return key.current;
  };
}

function LinkForm({
  projectId,
  initialUrl,
  shared,
  sharedFields,
  onCreated,
  onClose,
}: {
  projectId: string;
  initialUrl: string;
  shared: ReferenceSharedMetadata;
  sharedFields: ReactNode;
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const invalidate = useInvalidateReferences(projectId);
  const keyFor = useRequestKey();
  const [url, setUrl] = useState(initialUrl);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const touched = useRef({ title: false, description: false });
  const [error, setError] = useState<string | null>(null);

  const settled = useDebounced(url.trim(), 500);
  const ask = looksLikeUrl(settled) ? withScheme(settled) : null;
  const preview = useQuery({
    queryKey: ['references', projectId, 'link-preview', ask],
    queryFn: () => api.referenceLinkPreview(projectId, ask ?? ''),
    enabled: ask !== null,
    retry: false,
    staleTime: Infinity,
  });

  // What the page says about itself fills the fields nobody has typed in yet.
  useEffect(() => {
    const data = preview.data;
    if (!data) return;
    if (!touched.current.title) setTitle(data.title ?? '');
    if (!touched.current.description) setDescription(data.description ?? '');
  }, [preview.data]);

  const metadataFailed =
    ask !== null && (preview.isError || (preview.data !== undefined && preview.data.failure !== null));

  const save = useMutation({
    mutationFn: () => {
      const body: Omit<CreateReferenceInput, 'clientRequestId'> = {
        ...shared,
        sourceType: 'LINK',
        url: withScheme(url),
        ...(title.trim() !== '' ? { title: title.trim() } : {}),
        ...(description.trim() !== '' ? { description: description.trim() } : {}),
      };
      return api.createReference(projectId, { ...body, clientRequestId: keyFor(body) });
    },
    onSuccess: async (created) => {
      await invalidate();
      onCreated(created.id);
      onClose();
    },
    onError: (e) => {
      setError(errorText(e, 'The link could not be saved. Try again.'));
    },
  });

  return (
    <form
      className="ref-add-form"
      data-testid="ref-add-link-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (url.trim() === '') {
          setError('Paste or type the link first.');
          return;
        }
        setError(null);
        save.mutate();
      }}
    >
      <div className="ref-field">
        <label className="flbl" htmlFor="ref-add-url">
          Link
        </label>
        <input
          id="ref-add-url"
          className="ref-input"
          type="text"
          inputMode="url"
          autoComplete="off"
          data-autofocus
          value={url}
          maxLength={REFERENCE_URL_MAX}
          placeholder="https://"
          aria-describedby="ref-add-url-status"
          data-testid="ref-add-url"
          onChange={(event) => {
            setUrl(event.target.value);
            setError(null);
          }}
        />
        <p id="ref-add-url-status" className="ref-quiet" role="status" data-testid="ref-add-url-status">
          {preview.isFetching
            ? 'Reading the page…'
            : metadataFailed
              ? 'We could not read a title or description from this page. You can still save it, and add a title yourself.'
              : preview.data?.siteName
                ? `From ${preview.data.siteName}`
                : ''}
        </p>
      </div>
      <div className="ref-field">
        <label className="flbl" htmlFor="ref-add-title">
          Title
        </label>
        <input
          id="ref-add-title"
          className="ref-input"
          value={title}
          maxLength={REFERENCE_TITLE_MAX}
          placeholder="Taken from the page when left empty"
          data-testid="ref-add-title"
          onChange={(event) => {
            touched.current.title = true;
            setTitle(event.target.value);
          }}
        />
      </div>
      <div className="ref-field">
        <label className="flbl" htmlFor="ref-add-description">
          Description
        </label>
        <textarea
          id="ref-add-description"
          className="ref-textarea"
          rows={3}
          value={description}
          maxLength={REFERENCE_DESCRIPTION_MAX}
          placeholder="Why it was saved, what to look at"
          data-testid="ref-add-description"
          onChange={(event) => {
            touched.current.description = true;
            setDescription(event.target.value);
          }}
        />
      </div>
      {sharedFields}
      <FormFooter busy={save.isPending} error={error} label="Save reference" onCancel={onClose} />
    </form>
  );
}

function NoteForm({
  projectId,
  shared,
  sharedFields,
  onCreated,
  onClose,
}: {
  projectId: string;
  shared: ReferenceSharedMetadata;
  sharedFields: ReactNode;
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const invalidate = useInvalidateReferences(projectId);
  const keyFor = useRequestKey();
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const body: Omit<CreateReferenceInput, 'clientRequestId'> = {
        ...shared,
        sourceType: 'NOTE',
        note,
        ...(title.trim() !== '' ? { title: title.trim() } : {}),
      };
      return api.createReference(projectId, { ...body, clientRequestId: keyFor(body) });
    },
    onSuccess: async (created) => {
      await invalidate();
      onCreated(created.id);
      onClose();
    },
    onError: (e) => {
      setError(errorText(e, 'The note could not be saved. Try again.'));
    },
  });

  return (
    <form
      className="ref-add-form"
      data-testid="ref-add-note-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (note.trim() === '') {
          setMissing(true);
          return;
        }
        setError(null);
        save.mutate();
      }}
    >
      <div className="ref-field">
        <label className="flbl" htmlFor="ref-add-note">
          Note <span className="ref-required">(required)</span>
        </label>
        <textarea
          id="ref-add-note"
          className="ref-textarea"
          rows={6}
          data-autofocus
          value={note}
          maxLength={REFERENCE_NOTE_MAX}
          aria-invalid={missing}
          aria-describedby={missing ? 'ref-add-note-error' : undefined}
          placeholder="An idea, a piece of research, what a scene should feel like"
          data-testid="ref-add-note"
          onChange={(event) => {
            setNote(event.target.value);
            setMissing(false);
          }}
        />
        {missing ? (
          <p id="ref-add-note-error" className="ref-error" role="alert" data-testid="ref-add-note-error">
            Write the note first.
          </p>
        ) : null}
      </div>
      <div className="ref-field">
        <label className="flbl" htmlFor="ref-add-note-title">
          Title <span className="ref-required">(optional)</span>
        </label>
        <input
          id="ref-add-note-title"
          className="ref-input"
          value={title}
          maxLength={REFERENCE_TITLE_MAX}
          placeholder="The note's first line when left empty"
          data-testid="ref-add-note-title"
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
      </div>
      {sharedFields}
      <FormFooter busy={save.isPending} error={error} label="Save note" onCancel={onClose} />
    </form>
  );
}

function FormFooter({
  busy,
  error,
  label,
  onCancel,
}: {
  busy: boolean;
  error: string | null;
  label: string;
  onCancel: () => void;
}) {
  return (
    <div className="ref-add-footer">
      {error !== null ? (
        <p className="ref-error" role="alert" data-testid="ref-add-error">
          {error}
        </p>
      ) : null}
      <div className="ref-add-actions">
        <button type="submit" className="btn btn-primary" disabled={busy} data-testid="ref-add-save">
          {busy ? 'Saving…' : label}
        </button>
        <button type="button" className="ctl" data-testid="ref-add-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function UploadForm({
  projectId,
  meta,
  bulk,
  shared,
  sharedFields,
  onClose,
}: {
  projectId: string;
  meta: ReferenceLibraryMeta | undefined;
  bulk: BulkUpload;
  shared: ReferenceSharedMetadata;
  sharedFields: ReactNode;
  onClose: () => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const upload = meta?.upload;

  if (upload === undefined) {
    return (
      <p className="ref-quiet" data-testid="ref-add-upload-loading">
        Reading the upload limit…
      </p>
    );
  }

  if (!upload.allowed) {
    return (
      <div className="ref-plan-note" data-testid="ref-add-upload-plan">
        <p className="ref-plan-title">Uploading files is not part of this production’s plan.</p>
        <p className="ref-quiet">Links and notes can still be added. A plan with media storage adds uploads.</p>
        <Link to={`/projects/${projectId}/${TAB_SLUGS.BILLING}`} className="ctl ref-plan-link" data-testid="ref-add-upload-plan-link">
          See Plan &amp; billing
        </Link>
      </div>
    );
  }

  const { summary } = bulk;
  const waiting = bulk.items.filter((i) => i.state === 'queued' && i.metadata === null).length;
  const finished = summary.total > 0 && summary.active === 0 && summary.queued === 0;

  return (
    <div className="ref-add-form" data-testid="ref-add-upload-form">
      <div
        className="ref-drop ref-drop-large"
        data-over={over ? '1' : undefined}
        data-testid="ref-add-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          bulk.add(Array.from(event.dataTransfer.files));
        }}
      >
        <Icon name="cloudUpload" size={22} />
        <span className="ref-drop-text">
          <span>Drop files here, or</span>
          <span className="ref-drop-limit" data-testid="ref-add-limit">
            {uploadLimitText(upload)}
          </span>
        </span>
        <button
          type="button"
          className="ctl"
          data-autofocus
          data-testid="ref-add-choose"
          onClick={() => {
            picker.current?.click();
          }}
        >
          Choose files
        </button>
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          accept={acceptAttribute(upload)}
          data-testid="ref-add-file-input"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            bulk.add(files);
          }}
        />
      </div>

      <p className="ref-quiet">
        Images and video frames are shown as previews the server makes. The original files are kept privately and
        cannot be opened or downloaded here.
      </p>

      {bulk.rejected.length > 0 ? (
        <ul className="ref-upload-list" aria-label="Files that will not be uploaded" data-testid="ref-add-rejected">
          {bulk.rejected.map((r) => (
            <li key={r.key} className="ref-upload-item" data-state="rejected" data-testid={`ref-add-rejected-${r.name}`}>
              <span className="ref-upload-name">{r.name}</span>
              <span className="ref-upload-status" data-tone="danger">
                {r.reason}
              </span>
              <button
                type="button"
                className="ref-chip-x"
                aria-label={`Dismiss ${r.name}`}
                onClick={() => {
                  bulk.remove(r.key);
                }}
              >
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {bulk.items.length > 0 ? (
        <ul className="ref-upload-list" aria-label="Files" data-testid="ref-add-files">
          {bulk.items.map((item) => (
            <UploadRow key={item.key} item={item} onRemove={() => { bulk.remove(item.key); }} />
          ))}
        </ul>
      ) : null}

      {waiting > 0 || summary.total === 0 ? sharedFields : null}

      {summary.total > 0 ? (
        <p className="ref-upload-summary" role="status" aria-live="polite" data-testid="ref-add-summary">
          {uploadSummaryText(summary)}
        </p>
      ) : null}

      <div className="ref-add-footer">
      <div className="ref-add-actions">
        {waiting > 0 ? (
          <button
            type="button"
            className="btn btn-primary"
            data-testid="ref-add-upload-start"
            onClick={() => {
              bulk.start(shared);
            }}
          >
            {`Upload ${String(waiting)} file${waiting === 1 ? '' : 's'}`}
          </button>
        ) : null}
        {summary.failed > 0 && summary.active === 0 ? (
          <button type="button" className="ctl" data-testid="ref-add-retry-failed" onClick={bulk.retryFailed}>
            {`Retry failed (${String(summary.failed)})`}
          </button>
        ) : null}
        <button type="button" className="ctl" data-testid="ref-add-upload-close" onClick={onClose}>
          {finished ? 'Done' : summary.active > 0 ? 'Hide — uploads continue' : 'Cancel'}
        </button>
      </div>
      </div>
    </div>
  );
}

export function uploadSummaryText(summary: BulkUpload['summary']): string {
  const parts: string[] = [];
  if (summary.done > 0) parts.push(`${String(summary.done)} added`);
  if (summary.active > 0) parts.push(`${String(summary.active)} uploading`);
  const waiting = summary.queued;
  if (waiting > 0) parts.push(`${String(waiting)} waiting`);
  if (summary.failed > 0) parts.push(`${String(summary.failed)} failed`);
  return parts.length === 0 ? '' : parts.join(' · ');
}

function UploadRow({ item, onRemove }: { item: UploadItem; onRemove: () => void }) {
  const percent = item.total > 0 ? Math.min(100, Math.round((item.loaded / item.total) * 100)) : 0;
  return (
    <li className="ref-upload-item" data-state={item.state} data-testid={`ref-add-file-${item.file.name}`}>
      <span className="ref-upload-name">{item.file.name}</span>
      <span className="ref-upload-size">{formatBytes(item.file.size)}</span>
      {item.state === 'uploading' ? (
        <span
          className="ref-progress"
          role="progressbar"
          aria-label={`Uploading ${item.file.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          data-testid={`ref-add-progress-${item.file.name}`}
        >
          <span style={{ width: `${String(percent)}%` }} />
        </span>
      ) : null}
      <span
        className="ref-upload-status"
        data-tone={item.state === 'failed' ? 'danger' : item.state === 'done' ? 'ok' : undefined}
        data-testid={`ref-add-status-${item.file.name}`}
      >
        {item.state === 'queued'
          ? 'Waiting'
          : item.state === 'uploading'
            ? `${String(percent)}%`
            : item.state === 'done'
              ? 'Added'
              : (item.error ?? 'Failed')}
      </span>
      {item.state === 'queued' && item.metadata === null ? (
        <button type="button" className="ref-chip-x" aria-label={`Remove ${item.file.name}`} onClick={onRemove}>
          <Icon name="close" size={12} />
        </button>
      ) : null}
    </li>
  );
}
